<template>
	<div class="modal-mask">
		<div class="modal">
			<div class="modal-hdr">
				<h3>{{ editMode ? (hasLocation ? "Modifica posizione" : "Inserisci nuove coordinate") : "Dettagli posizione" }}</h3>
				<button class="ico-btn" @click="handleClose">✕</button>
			</div>

			<p class="modal-sub">
				Coordinate per <strong>{{ deviceName || "device" }}</strong>
			</p>

			<!-- View mode -->
			<template v-if="!editMode">
				<div class="coord-grid">
					<div>
						<span class="lbl">Latitudine</span>
						<span class="val mono">{{ location ? formatLat(location[0]) : "—" }}</span>
					</div>
					<div>
						<span class="lbl">Longitudine</span>
						<span class="val mono">{{ location ? formatLng(location[1]) : "—" }}</span>
					</div>
					<div class="span-2">
						<span class="lbl">Raw</span>
						<span class="val mono">
							{{ location ? `${location[0].toFixed(6)}, ${location[1].toFixed(6)}` : "—" }}
						</span>
					</div>
				</div>

				<div class="modal-actions">
					<button @click="$emit('copy')" :disabled="!location">Copia</button>
					<button v-if="deviceId" class="btn-primary" @click="openEdit">{{ hasLocation ? "Modifica" : "Inserisci coordinate" }}</button>
				</div>
			</template>

			<!-- Edit mode -->
			<template v-else>
				<div class="coord-grid">
					<div>
						<span class="lbl">Latitudine</span>
						<input
							v-model="inputLat"
							class="coord-input mono"
							type="number"
							step="any"
							placeholder="es. 45.410000"
							:class="{ invalid: latError }"
							@keydown="handleArrow($event, inputLat, v => inputLat = v)"
						/>
						<span v-if="latError" class="field-error">{{ latError }}</span>
					</div>
					<div>
						<span class="lbl">Longitudine</span>
						<input
							v-model="inputLng"
							class="coord-input mono"
							type="number"
							step="any"
							placeholder="es. 11.880000"
							:class="{ invalid: lngError }"
							@keydown="handleArrow($event, inputLng, v => inputLng = v)"
						/>
						<span v-if="lngError" class="field-error">{{ lngError }}</span>
					</div>
				</div>

				<div class="modal-actions">
					<button @click="editMode = false" :disabled="saving">Annulla</button>
					<button class="btn-primary" @click="saveLocation" :disabled="saving">
						{{ saving ? "Salvataggio…" : "Salva" }}
					</button>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
	import axios from "@/axiosInstance";
	import { ref, computed } from "vue";
	import { useToast } from "vue-toast-notification";

	const props = defineProps<{
		deviceName: string;
		deviceId?: number;
		location?: [number, number] | undefined;
	}>();

	const emit = defineEmits<{
		(e: "close"): void;
		(e: "copy"): void;
		(e: "saved", location: [number, number], updatedAt: string): void;
	}>();

	const toast = useToast();
	const hasLocation = computed(() => Array.isArray(props.location) && props.location.length === 2);
	const editMode = ref(!hasLocation.value); // apre direttamente in edit se non ci sono coordinate
	const saving = ref(false);
	const inputLat = ref(hasLocation.value ? String(props.location![0]) : "");
	const inputLng = ref(hasLocation.value ? String(props.location![1]) : "");
	const latError = ref("");
	const lngError = ref("");

	const formatLat = (lat: number) => `${lat >= 0 ? "N" : "S"} ${Math.abs(lat).toFixed(6)}`;
	const formatLng = (lng: number) => `${lng >= 0 ? "E" : "W"} ${Math.abs(lng).toFixed(6)}`;

	function handleArrow(e: KeyboardEvent, current: string, set: (v: string) => void) {
		if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
		e.preventDefault();
		const val = parseFloat(current);
		if (isNaN(val)) return;
		const decimals = current.includes(".") ? current.split(".")[1].length : 6;
		const precision = Math.max(decimals, 6);
		const delta = e.key === "ArrowUp" ? 0.000001 : -0.000001;
		set((val + delta).toFixed(precision));
	}

	function openEdit() {
		inputLat.value = props.location ? String(props.location[0]) : "";
		inputLng.value = props.location ? String(props.location[1]) : "";
		latError.value = "";
		lngError.value = "";
		editMode.value = true;
	}

	function validate(): boolean {
		latError.value = "";
		lngError.value = "";

		const lat = parseFloat(inputLat.value);
		const lng = parseFloat(inputLng.value);

		let ok = true;
		if (isNaN(lat) || lat < -90 || lat > 90) {
			latError.value = "Valore non valido (−90 … 90)";
			ok = false;
		}
		if (isNaN(lng) || lng < -180 || lng > 180) {
			lngError.value = "Valore non valido (−180 … 180)";
			ok = false;
		}
		return ok;
	}

	async function saveLocation() {
		if (!validate()) return;

		const lat = parseFloat(inputLat.value);
		const lng = parseFloat(inputLng.value);

		saving.value = true;
		try {
			const { data } = await axios.put(`/devices/${props.deviceId}/save-location`, {
				location: [lat, lng],
			});
			toast.success("Posizione aggiornata", { duration: 2500 });
			const updatedAt = data?.graph?.updated_at ?? new Date().toISOString();
			emit("saved", [lat, lng], updatedAt);
			editMode.value = false;
		} catch (err: any) {
			const msg = err?.response?.data?.message ?? "Errore durante il salvataggio";
			toast.error(msg, { duration: 3000 });
		} finally {
			saving.value = false;
		}
	}

	function handleClose() {
		editMode.value = false;
		emit("close");
	}
</script>

<style scoped>
.coord-input {
	width: 100%;
	margin-top: 4px;
	padding: 6px 10px;
	background: #1c2229;
	border: 1px solid #2a2f34;
	border-radius: 6px;
	color: #e2e8f0;
	font-size: 0.875rem;
}

.coord-input:focus {
	outline: none;
	border-color: #22c55e;
}

.coord-input.invalid {
	border-color: #ef4444;
}

.field-error {
	display: block;
	margin-top: 3px;
	font-size: 0.75rem;
	color: #ef4444;
}

.btn-primary {
	background: #22c55e;
	color: #0f1117;
	border: none;
	font-weight: 600;
}

.btn-primary:hover:not(:disabled) {
	background: #16a34a;
}
</style>
